import { useMemo, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';

import { DEFAULT_PAGE_INDEX } from '@/app/constants/pagination';
import { projectDetailsQueryOptions } from '@/app/queries/projects';
import { Button } from '@/common/components/elements/button';
import { Title } from '@/common/components/elements/title';
import { EmptyState } from '@/common/components/features/empty-state';
import { Can } from '@/common/components/features/role-management';
import { CreatePodcastManager } from '@/modules/podcasts/components/create-podcast-manager';
import { PodcastList } from '@/modules/podcasts/components/podcast-list';
import { PodcastMobileList } from '@/modules/podcasts/components/podcast-list/podcast-mobile-list';
import { useGetPaginatedPodcastsQuery } from '@/modules/podcasts/queries/use-get-paginated-podcasts-query';

const PAGE_SIZE = import.meta.env.MODE === 'test' ? 3 : 10;

const Project = () => {
  const { projeto: projectId } = Route.useParams();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: PAGE_SIZE,
  });

  const { data: projectDetails } = useSuspenseQuery(projectDetailsQueryOptions(projectId));

  const {
    data: podcasts,
    error: errorPodcast,
    refetch: refetchPodcasts,
    isLoading: isLoadingPodcasts,
  } = useGetPaginatedPodcastsQuery({
    projectId,
    pagination,
  });

  const isEmptyPodcastList = useMemo(
    () => !podcasts?.data || podcasts?.data?.length === 0,
    [podcasts?.data],
  );

  const projectName = useMemo(() => projectDetails?.name, [projectDetails?.name]);

  const hasPodcastError = useMemo(
    () => !!errorPodcast && !isLoadingPodcasts,
    [errorPodcast, isLoadingPodcasts],
  );

  const isEmptyList = useMemo(
    () => isEmptyPodcastList && !errorPodcast && !isLoadingPodcasts,
    [isEmptyPodcastList, errorPodcast, isLoadingPodcasts],
  );

  const shouldShowTable = useMemo(
    () => isLoadingPodcasts || (!errorPodcast && !isEmptyPodcastList),
    [isLoadingPodcasts, errorPodcast, isEmptyPodcastList],
  );
  return (
    <div className='flex flex-col w-full h-full gap-6'>
      <div className='flex flex-col gap-3'>
        <div className='flex flex-col sm:items-center sm:justify-between sm:flex-row'>
          <Title className='capitalize'>{projectName?.toLocaleLowerCase()}</Title>

          <Can I='read' a='Member'>
            <div className='items-center gap-2'>
              <Link
                to='/projetos/$projeto/membros'
                params={{ projeto: projectId }}
                className='text-base cursor-pointer'
              >
                Ver membros
              </Link>
            </div>
          </Can>
        </div>

        <span className='flex items-center'>
          <span className='h-px flex-1 bg-border-neutral'></span>
        </span>

        <div className='flex flex-col gap-6'>
          <div className='flex items-center justify-between'>
            <h3 className='text-2xl text-content-neutral-primary font-medium'>Podcasts</h3>

            <Can I='create' a='Podcast'>
              <CreatePodcastManager projectId={projectId}>
                {({ open }) => (
                  <Button animated size='small' variant='primary' onClick={open}>
                    Criar novo
                  </Button>
                )}
              </CreatePodcastManager>
            </Can>
          </div>

          {shouldShowTable && (
            <div>
              <div className='hidden sm:flex'>
                <PodcastList
                  projectId={projectId}
                  data={podcasts?.data}
                  pagination={pagination}
                  loading={isLoadingPodcasts}
                  setPagination={setPagination}
                  totalItems={podcasts?.totalItems}
                />
              </div>
              <div className='sm:hidden'>
                <PodcastMobileList
                  data={podcasts?.data}
                  pagination={pagination}
                  loading={isLoadingPodcasts}
                  setPagination={setPagination}
                  totalItems={podcasts?.totalItems}
                />
              </div>
            </div>
          )}

          {hasPodcastError && (
            <EmptyState
              image={{
                src: '/images/empty-image.webp',
                alt: 'Ilustração representando erro ao carregar podcasts',
              }}
              title='Erro ao carregar podcasts'
              description='Ocorreu um problema ao buscar os podcasts do projeto. Verifique sua conexão com a internet e tente novamente.'
              actions={
                <Button size='large' variant='primary' onClick={() => refetchPodcasts()}>
                  Tentar novamente
                </Button>
              }
            />
          )}

          {isEmptyList && (
            <EmptyState
              image={{
                src: '/images/empty-image.webp',
                alt: 'Ilustração representando ausência de podcasts',
              }}
              title='Nenhum podcast encontrado'
              description='Este projeto ainda não possui podcasts cadastrados. Adicione o primeiro para começar.'
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_app/_dashboard/projetos/$projeto/')({
  component: Project,
});
